/**
 * Resolving the numbers inside a targeted-feedback trigger.
 *
 * A trigger is a string: `exact:25`, `range:10:20`, and with Slider's handle prefix `b:exact:25`.
 * Every evaluator reads those numbers with `parseFloat`, which is correct right up until the editor
 * lets a teacher write an EQUATION into one.
 *
 * Slider's editor does exactly that — `SliderTriggerEditor` renders a `FormulaField` for all three
 * of its trigger inputs — and then `evaluatorDataFrom` passes `feedback: d.feedback` straight
 * through unresolved. So `exact:{n}*5` reaches `parseFloat`, becomes NaN, and the trigger silently
 * never fires. The teacher authors the feedback, sees it listed in the panel, and no student ever
 * receives it. Verified against the real evaluator: a literal trigger fires, the same trigger
 * written as an equation returns nothing.
 *
 * This is the missing step, in one place rather than one per block, because Numerical is about to
 * grow the same editor and copying a broken seam is how the last twelve duplications happened.
 */
/**
 * A segment that is ALREADY a plain number, matched strictly.
 *
 * `parseFloat` is the wrong test here and the tests caught it: `parseFloat('2*(3+4)')` is 2, a
 * perfectly finite number, so an expression starting with a digit was mistaken for a literal and
 * passed through unresolved. Anchored, so only a whole numeric literal qualifies.
 */
const PLAIN_NUMBER = /^-?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?$/;
/** The condition types whose trailing segments are VALUES rather than identifiers. */
const VALUE_CONDITIONS = new Set(['exact', 'lt', 'lte', 'gt', 'gte', 'range']);
/**
 * Rewrites a trigger's value segments through `resolve`, leaving everything else untouched.
 *
 * Deliberately locates the condition type rather than assuming a position, so Slider's `b:` handle
 * prefix needs no special case and a trigger that is not value-shaped at all — `unit_<id>`,
 * `math_error_<category>`, `option_<id>` — passes through by simply never matching.
 *
 * A segment that is already a plain number is left EXACTLY as written. Resolving it would be a
 * no-op numerically but could still reformat it (`5.0` to `5`), and a literal trigger's behaviour
 * should be bit-identical before and after this function exists.
 *
 * A formula that fails to resolve also keeps its original text. `exact:NaN` and `exact:{n}*5` both
 * fail to match, so nothing is gained by rewriting it, and the raw text is what a developer needs
 * to see when working out why a state never fired.
 */
export function resolveTriggerValues(trigger, resolve) {
    const parts = trigger.split(':');
    const at = parts.findIndex(p => VALUE_CONDITIONS.has(p));
    if (at === -1)
        return trigger;
    let changed = false;
    const out = parts.map((part, i) => {
        if (i <= at || part === '')
            return part;
        if (PLAIN_NUMBER.test(part))
            return part;
        const n = resolve(part);
        if (!Number.isFinite(n))
            return part;
        changed = true;
        return String(n);
    });
    return changed ? out.join(':') : trigger;
}
/**
 * The same pass over a whole feedback list, which is how both blocks' `evaluatorDataFrom` uses it.
 *
 * The `targeted` filter is belt-and-braces and known to be so: `resolveTriggerValues` already
 * returns a name like `correct` untouched, and a mutation removing this check survives the suite
 * for exactly that reason. It stays because it states the intent at the point of use — a default
 * state has no value to resolve — not because it is load-bearing.
 */
export function resolveFeedbackTriggers(states, 
/**
 * `fieldId` is this trigger's own, so a resolver can read the angle mode the FIELD declares
 * rather than one mode for the whole block. A caller with nothing per-field to say can still
 * pass a one-argument function and ignore it.
 */
resolve) {
    return states.map(s => s.type === 'targeted'
        ? { ...s, trigger: resolveTriggerValues(s.trigger, v => resolve(v, `feedback.${s.id}.trigger`)) }
        : s);
}
/**
 * A value trigger's condition keyword and its value segments, or null if it is not value-shaped.
 *
 * Located the same way `resolveTriggerValues` locates it — by finding the condition keyword rather
 * than assuming a position — so Slider's `b:` handle prefix needs no special case and `unit_<id>`,
 * `math_error_<cat>` and `option_<id>` fall out by simply never matching.
 */
export function valueTriggerParts(trigger) {
    const parts = trigger.split(':');
    const at = parts.findIndex(p => VALUE_CONDITIONS.has(p));
    if (at === -1)
        return null;
    return { type: parts[at], values: parts.slice(at + 1) };
}
/**
 * A trigger value that cannot become a number, and is not merely waiting for one.
 *
 * This used to stop at the NAMES: an undeclared `{nope}`, or arithmetic that is not arithmetic. It
 * had to, because validation ran against an empty roll — a display surface shows `{test}`, not one
 * sample of it — so "did not resolve here" said nothing about whether it ever could. The cost was a
 * blind spot with nothing else covering it: `{n}/0` names only real variables, so it passed, and
 * then produced NaN for every student who ever met it. A condition that can never fire, reported by
 * no one.
 *
 * A probe roll closes it. With one real assignment to hand, "does this resolve" is finally a
 * question with an answer, and a formula that fails at the probe fails everywhere — nothing in a
 * domain makes `x/0` finite.
 *
 * `probeable` is what keeps the old caution where it is still warranted. A variable can be declared
 * and have no range yet, in which case the probe has no value for it and this genuinely cannot
 * judge the expression. That is the variables panel's problem to report, not this condition's, so
 * it declines rather than accusing.
 */
export function triggerValueIsDead(value, declaredVariables, probeable, resolveAtProbe) {
    if (value.trim() === '' || Number.isFinite(parseFloat(value)))
        return false;
    const names = [...value.matchAll(/\{([^}]+)\}/g)].map(m => m[1].trim());
    if (names.length === 0)
        return true;
    if (!names.every(n => declaredVariables.has(n)))
        return true;
    if (!names.every(n => probeable.has(n)))
        return false;
    return !Number.isFinite(resolveAtProbe(value));
}
/**
 * At most ONE fault per condition, worst first.
 *
 * A half-typed condition can trip several of these at once, and a card stacking three notes about
 * one unfinished field is noise. Authored-and-broken beats unfinished: a formula that cannot
 * resolve is a thing to fix, while a blank half of a range is a thing to finish, and saying both
 * about one field buries the first.
 *
 * A block-level report still shows every KIND present somewhere, because different conditions can
 * fail differently — it counts these, rather than replacing them.
 */
export function triggerFault(trigger, ctx) {
    const parsed = valueTriggerParts(resolveTriggerValues(trigger, ctx.resolve));
    if (!parsed)
        return null;
    const [v1 = '', v2 = ''] = parsed.values;
    const dead = (v) => triggerValueIsDead(v, ctx.declared, ctx.probeable, ctx.resolve);
    if (dead(v1) || (parsed.type === 'range' && dead(v2)))
        return 'unresolvable';
    if (parsed.type === 'range') {
        if (v1.trim() === '' || v2.trim() === '')
            return 'range-incomplete';
        const lo = parseFloat(v1), hi = parseFloat(v2);
        if (!ctx.allowWrap && Number.isFinite(lo) && Number.isFinite(hi) && lo > hi)
            return 'range-inverted';
        return null;
    }
    return v1.trim() === '' ? 'no-value' : null;
}
//# sourceMappingURL=feedback-trigger.js.map